import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { Template, etapasFunil, Tarefa } from '@/types/tarefas';
import { Lead } from '@/types/leads';
import { Loader2, Plus, FileText, Clock, AlertCircle, CheckCircle2 } from "lucide-react";

interface AdicionarTarefaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function AdicionarTarefaModal({ open, onOpenChange, onSuccess }: AdicionarTarefaModalProps) {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedLeadId, setSelectedLeadId] = useState<string>('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingTarefas, setLoadingTarefas] = useState(false);
  const [creating, setCreating] = useState(false);
  const [tarefasExistentes, setTarefasExistentes] = useState<Tarefa[]>([]);
  const { toast } = useToast();

  const selectedLead = leads.find(l => l.id === selectedLeadId);
  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  // Buscar leads quando o modal abrir
  useEffect(() => {
    if (open) {
      fetchLeads();
    } else {
      // Resetar estado quando fechar
      setSelectedLeadId('');
      setSelectedTemplateId('');
      setTemplates([]);
      setTarefasExistentes([]);
    }
  }, [open]);

  // Buscar templates e tarefas existentes quando um lead for selecionado
  useEffect(() => {
    if (selectedLeadId && selectedLead) {
      fetchTemplates(selectedLead.etapa);
      fetchTarefasExistentes(selectedLeadId);
    } else {
      setTemplates([]);
      setSelectedTemplateId('');
      setTarefasExistentes([]);
    }
  }, [selectedLeadId, selectedLead]);

  const fetchLeads = async () => {
    try {
      setLoadingLeads(true);
      const response = await fetchWithAuth('/api/leads?crm_funil=vendas');
      if (!response.ok) {
        throw new Error('Erro ao buscar leads');
      }
      const data: Lead[] = await response.json();
      setLeads(data);
    } catch (error: any) {
      console.error('Erro ao buscar leads:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao buscar leads',
        variant: 'destructive'
      });
    } finally {
      setLoadingLeads(false);
    }
  };

  const fetchTemplates = async (etapa: string) => {
    try {
      setLoadingTemplates(true);
      const params = new URLSearchParams();
      params.append('etapa_funil', etapa);
      params.append('ativo', 'true');

      const response = await fetchWithAuth(`/api/tarefas-templates?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Erro ao buscar templates');
      }
      const data: Template[] = await response.json();
      setTemplates(data);
    } catch (error: any) {
      console.error('Erro ao buscar templates:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao buscar templates de tarefas',
        variant: 'destructive'
      });
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchTarefasExistentes = async (leadId: string) => {
    try {
      setLoadingTarefas(true);
      const params = new URLSearchParams();
      params.append('lead_id', leadId);

      const response = await fetchWithAuth(`/api/tarefas?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Erro ao buscar tarefas existentes');
      }
      const data: Tarefa[] = await response.json();
      // Filtrar apenas tarefas não canceladas (inclui concluídas)
      // Não permitir criar tarefas duplicadas mesmo que a anterior tenha sido concluída
      const tarefasNaoCanceladas = data.filter(t => t.status !== 'cancelada');
      setTarefasExistentes(tarefasNaoCanceladas);
    } catch (error: any) {
      console.error('Erro ao buscar tarefas existentes:', error);
      // Não mostrar toast de erro aqui, pois é uma validação silenciosa
      setTarefasExistentes([]);
    } finally {
      setLoadingTarefas(false);
    }
  };

  // Verificar se um template já tem uma tarefa existente (incluindo concluídas)
  const templateTemTarefaAtiva = (templateId: string): boolean => {
    return tarefasExistentes.some(t => t.template_id === templateId);
  };

  // Obter a tarefa existente de um template
  const getTarefaExistente = (templateId: string): Tarefa | undefined => {
    return tarefasExistentes.find(t => t.template_id === templateId);
  };

  // Obter label do status
  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      'pendente': 'Pendente',
      'em_andamento': 'Em Andamento',
      'atrasada': 'Atrasada',
      'concluida': 'Concluída',
      'concluida_com_pendencias': 'Concluída com Pendências',
      'cancelada': 'Cancelada'
    };
    return labels[status] || status;
  };

  const handleCriarTarefa = async () => {
    if (!selectedLeadId || !selectedTemplateId) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Selecione um lead e um template de tarefa',
        variant: 'destructive'
      });
      return;
    }

    // Verificar se já existe uma tarefa para este template e lead (incluindo concluídas)
    if (templateTemTarefaAtiva(selectedTemplateId)) {
      const tarefaExistente = getTarefaExistente(selectedTemplateId);
      const statusLabel = tarefaExistente ? getStatusLabel(tarefaExistente.status) : 'Existente';
      toast({
        title: 'Tarefa já existe',
        description: `Já existe uma tarefa "${tarefaExistente?.titulo || 'desconhecida'}" (Status: ${statusLabel}) para este lead. Não é possível criar tarefas duplicadas, mesmo que a anterior tenha sido concluída.`,
        variant: 'destructive',
        duration: 5000
      });
      return;
    }

    try {
      setCreating(true);
      const response = await fetchWithAuth('/api/tarefas', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lead_id: selectedLeadId,
          template_id: selectedTemplateId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        const errorMessage = errorData.error || 'Erro ao criar tarefa';
        
        // Se for erro de conflito (tarefa duplicada), destacar melhor
        if (response.status === 409) {
          toast({
            title: 'Tarefa já existe',
            description: errorMessage,
            variant: 'destructive',
            duration: 5000
          });
          throw new Error(errorMessage);
        }
        
        throw new Error(errorMessage);
      }

      toast({
        title: 'Sucesso',
        description: 'Tarefa criada com sucesso!'
      });

      // Resetar seleções
      setSelectedLeadId('');
      setSelectedTemplateId('');
      setTemplates([]);

      // Fechar modal e chamar callback
      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Erro ao criar tarefa:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao criar tarefa',
        variant: 'destructive'
      });
    } finally {
      setCreating(false);
    }
  };

  const getEtapaLabel = (etapa: string) => {
    const etapaObj = etapasFunil.find(e => e.value === etapa);
    return etapaObj?.label || etapa;
  };

  const getPrioridadeBadge = (prioridade: string) => {
    switch (prioridade) {
      case 'urgente':
        return <Badge className="bg-red-600 text-white">Urgente</Badge>;
      case 'alta':
        return <Badge className="bg-orange-500 text-white">Alta</Badge>;
      case 'media':
        return <Badge className="bg-yellow-500 text-white">Média</Badge>;
      case 'baixa':
        return <Badge variant="secondary">Baixa</Badge>;
      default:
        return <Badge variant="secondary">{prioridade}</Badge>;
    }
  };

  const getTipoTarefaLabel = (tipo: string) => {
    switch (tipo) {
      case 'acao':
        return 'Ação';
      case 'pergunta':
        return 'Pergunta';
      case 'checklist':
        return 'Checklist';
      default:
        return tipo;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Adicionar Nova Tarefa</DialogTitle>
          <DialogDescription>
            Selecione um lead e escolha um template de tarefa para criar uma nova tarefa
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Seleção de Lead */}
          <div className="space-y-2">
            <Label htmlFor="lead">Lead *</Label>
            <Select value={selectedLeadId} onValueChange={setSelectedLeadId} disabled={loadingLeads}>
              <SelectTrigger id="lead">
                <SelectValue placeholder={loadingLeads ? "Carregando leads..." : "Selecione um lead"} />
              </SelectTrigger>
              <SelectContent>
                {leads.map((lead) => (
                  <SelectItem key={lead.id} value={lead.id}>
                    {lead.nome} {lead.razaoSocial && `(${lead.razaoSocial})`} - {getEtapaLabel(lead.etapa)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedLead && (
              <div className="text-sm text-muted-foreground mt-1">
                <p><strong>Etapa atual:</strong> {getEtapaLabel(selectedLead.etapa)}</p>
                <p><strong>Email:</strong> {selectedLead.email}</p>
                <p><strong>Telefone:</strong> {selectedLead.telefone}</p>
              </div>
            )}
          </div>

          {/* Lista de Templates Disponíveis */}
          {selectedLeadId && (
            <div className="space-y-2">
              <Label>Templates de Tarefas Disponíveis para esta Etapa</Label>
              {loadingTemplates ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-sm text-muted-foreground">Carregando templates...</span>
                </div>
              ) : templates.length === 0 ? (
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center text-muted-foreground">
                      <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>Nenhum template de tarefa disponível para a etapa "{getEtapaLabel(selectedLead?.etapa || '')}"</p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-3">
                    {templates.map((template) => {
                      const temTarefaAtiva = templateTemTarefaAtiva(template.id);
                      const tarefaExistente = getTarefaExistente(template.id);
                      const isDisabled = temTarefaAtiva;
                      
                      return (
                        <Card
                          key={template.id}
                          className={`transition-all ${
                            isDisabled 
                              ? 'opacity-60 cursor-not-allowed bg-muted/50' 
                              : 'cursor-pointer hover:shadow-md'
                          } ${
                            selectedTemplateId === template.id && !isDisabled ? 'ring-2 ring-primary' : ''
                          }`}
                          onClick={() => !isDisabled && setSelectedTemplateId(template.id)}
                        >
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <CardTitle className="text-base flex items-center gap-2">
                                <FileText className="h-4 w-4" />
                                {template.titulo}
                                {temTarefaAtiva && (
                                  <Badge variant="outline" className="ml-2 text-xs">
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    Já existe
                                  </Badge>
                                )}
                              </CardTitle>
                              {template.descricao && (
                                <CardDescription className="mt-1">{template.descricao}</CardDescription>
                              )}
                              {temTarefaAtiva && tarefaExistente && (
                                <div className="mt-2 text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                                  <p><strong>Tarefa existente:</strong> Status: {getStatusLabel(tarefaExistente.status)}</p>
                                  {tarefaExistente.data_vencimento && (
                                    <p className="mt-1">Vencimento: {new Date(tarefaExistente.data_vencimento).toLocaleDateString('pt-BR')}</p>
                                  )}
                                  {(tarefaExistente.status === 'concluida' || tarefaExistente.status === 'concluida_com_pendencias') && (
                                    <p className="mt-1 text-orange-600 font-medium">Não é possível criar tarefas duplicadas mesmo que a anterior tenha sido concluída.</p>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col gap-1 items-end">
                              {getPrioridadeBadge(template.prioridade)}
                              <Badge variant="outline">{getTipoTarefaLabel(template.tipo_tarefa)}</Badge>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                            {template.prazo_horas && (
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                <span>Prazo: {template.prazo_horas} hora(s)</span>
                              </div>
                            )}
                          </div>
                          {template.instrucoes && (
                            <div className="mt-2 text-sm text-muted-foreground">
                              <p className="line-clamp-2">{template.instrucoes}</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                    })}
                  </div>
                </ScrollArea>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={creating}>
            Cancelar
          </Button>
          <Button
            onClick={handleCriarTarefa}
            disabled={!selectedLeadId || !selectedTemplateId || creating || (selectedTemplateId && templateTemTarefaAtiva(selectedTemplateId))}
          >
            {creating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Criando...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Criar Tarefa
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}





